import React, { useState, useEffect } from "react";
// Added message to the imports for popups
import { Row, Col, Card, Button, Modal, Input, Select, message } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import ResponsiveTable from "../components/ResponsiveTable";
import LoadingSpinner from "../components/LoadingSpinner";

const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const UserManagement = () => {
  const [data, setData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [newRecord, setNewRecord] = useState({
    username: "",
    email: "",
    role: "",
    projectId: "",
    projectName: "",
    status: "Active",
  });
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // Fetch users from the API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/users`);
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to fetch users:", errorText);
          throw new Error("Failed to fetch users");
        }

        const result = await response.json();
        setData(
          result.users.map((user) => ({
            key: user.user_id,
            username: user.username,
            email: user.email,
            role: user.role,
            projectId: user.project_id,
            projectName: user.project_name,
            status: user.status.charAt(0).toUpperCase() + user.status.slice(1), // Capitalize status
          }))
        );
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchUsers();

    const interval = setInterval(fetchUsers, 30000); // Auto-refresh every 30 seconds
    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  // Fetch projects for the dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/projects/`);
        if (response.ok) {
          const result = await response.json();
          setProjects(result);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  // Filter data based on search text (FIXED: Added .trim() to ignore accidental spaces)
  useEffect(() => {
    const searchLower = searchText.trim().toLowerCase();
    setFilteredData(
      data.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchLower)
        )
      )
    );
  }, [searchText, data]);

  const toggleStatus = async (key) => {
    const record = data.find((item) => item.key === key);
    if (record) {
      try {
        const updatedStatus = record.status === "Active" ? "inactive" : "active";

        const requestBody = {
          email: record.email,
          status: updatedStatus,
        };

        const response = await fetch(`${API_BASE_URL}/status/update_user_status`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to update user status:", errorText);
          throw new Error("Failed to update user status");
        }

        // Update the status locally without requiring a page refresh
        setData((prevData) =>
          prevData.map((item) =>
            item.key === key
              ? { ...item, status: updatedStatus.charAt(0).toUpperCase() + updatedStatus.slice(1) }
              : item
          )
        );
      } catch (error) {
        console.error("Error updating user status:", error);
      }
    } else {
      console.error("User not found for key:", key);
    }
  };

  const deleteRow = async (key) => {
    const record = data.find((item) => item.key === key);
    if (record) {
      try {
        const requestBody = { email: record.email };

        const response = await fetch(`${API_BASE_URL}/users/delete_user`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          message.error("Failed to delete user. Please try again.");
          const errorText = await response.text();
          console.error("Failed to delete user:", errorText);
          throw new Error("Failed to delete user");
        }

        // Popup success message
        message.success("User and their sessions deleted successfully!");

        // Update the data state to remove the deleted user
        setData((prevData) => prevData.filter((item) => item.key !== key));
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    } else {
      console.error("User not found for key:", key);
    }
  };

  const editRow = (record) => {
    setIsEditing(true);
    setEditingRecord({ ...record });
  };

  const saveEdit = async () => {
    try {
      const requestBody = {
        user_id: editingRecord.key, // Sends ID to bypass the DB duplicate email error
        email: editingRecord.email,
        username: editingRecord.username,
        role: editingRecord.role,
        project_id: editingRecord.projectId,
        project_name: editingRecord.projectName,
      };

      const response = await fetch(`${API_BASE_URL}/users/update_user`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        message.error("Failed to update user. Please try again.");
        const errorText = await response.text();
        console.error("Failed to update user:", errorText);
        throw new Error("Failed to update user");
      }

      // Popup success message
      message.success("User updated successfully!");

      // Update the data state with the updated user details locally
      setData((prevData) =>
        prevData.map((item) =>
          item.key === editingRecord.key
            ? {
              ...item,
              username: editingRecord.username,
              role: editingRecord.role,
              projectId: editingRecord.projectId,
              projectName: editingRecord.projectName,
            }
            : item
        )
      );

      setIsEditing(false);
      setEditingRecord(null);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const addNewRecord = async () => {
    try {
      const requestBody = {
        username: newRecord.username,
        email: newRecord.email,
        role: newRecord.role,
        project_id: newRecord.projectId, // Ensure project_id is included
        project_name: newRecord.projectName,
      };

      const response = await fetch(`${API_BASE_URL}/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to add user:", errorText);
        throw new Error("Failed to add user");
      }

      const user = await response.json();

      // Ensure the key is set correctly
      setData((prevData) => [
        ...prevData,
        {
          key: user.user_id,
          username: user.username,
          email: user.email,
          role: user.role,
          projectName: user.project_name,
          status: "Active", // Default status
        },
      ]);

      setIsAdding(false);
      setNewRecord({
        username: "",
        email: "",
        role: "",
        projectId: "",
        projectName: "",
        status: "Active",
      });
    } catch (error) {
      console.error("Error adding user:", error);
    }
  };

  const columns = [
    {
      title: "USERNAME",
      dataIndex: "username",
      key: "username",
      sorter: (a, b) => a.username.localeCompare(b.username), // Sorting by username
    },
    {
      title: "EMAIL",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email), // Sorting by email
    },
    {
      title: "ROLE",
      dataIndex: "role",
      key: "role",
      // Removed sorting for role
    },
    {
      title: "PROJECT NAME",
      dataIndex: "projectName",
      key: "projectName",
      sorter: (a, b) => a.projectName.localeCompare(b.projectName), // Sorting by project name
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      // Removed sorting for status
      render: (status, record) => (
        <Button
          type="primary"
          style={{
            backgroundColor: status === "Active" ? "#1890ff" : "#d9d9d9", // Blue for "Active", default for others
            borderColor: status === "Active" ? "#1890ff" : "#d9d9d9",
            color: status === "Active" ? "#fff" : "#000",
          }}
          onClick={() => toggleStatus(record.key)}
        >
          {status}
        </Button>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => editRow(record)}
            style={{ marginRight: 8, color: "#1890ff", borderColor: "#1890ff" }}
          >
            Edit
          </Button>
          <Button onClick={() => deleteRow(record.key)} danger>
            ❌ Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="user-management">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title="User Management Table" // Added title
            extra={
              <>
                <Input
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200, marginRight: 8 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAdding(true)}
                >
                  Add User
                </Button>
              </>
            }
          >
            {isInitialLoad ? (
              <LoadingSpinner text="Loading records..." />
            ) : (
              <div className="table-responsive">
                <ResponsiveTable
                  columns={columns}
                  dataSource={filteredData}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1000 }}
                  className="ant-border-space"
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>
      {/* Edit Modal */}
      <Modal
        title="Edit User"
        visible={isEditing}
        onOk={saveEdit}
        onCancel={() => {
          setIsEditing(false);
          setEditingRecord(null);
        }}
      >
        <Input
          placeholder="Username"
          value={editingRecord?.username}
          onChange={(e) => setEditingRecord({ ...editingRecord, username: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Email"
          value={editingRecord?.email}
          disabled
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Role"
          value={editingRecord?.role}
          onChange={(e) => setEditingRecord({ ...editingRecord, role: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Select
          placeholder="Select Project ID"
          value={editingRecord?.projectId || undefined}
          style={{ width: '100%', marginBottom: 8 }}
          onChange={(value) => {
            const selectedProj = projects.find((p) => p.project_id === value);
            if (selectedProj) {
              setEditingRecord({
                ...editingRecord,
                projectId: selectedProj.project_id,
                projectName: selectedProj.project_name
              });
            }
          }}
        >
          {projects.map((proj) => (
            <Option key={proj.project_id} value={proj.project_id}>
              {proj.project_id}
            </Option>
          ))}
        </Select>
        <Input
          placeholder="Project Name"
          value={editingRecord?.projectName}
          disabled
          style={{ marginBottom: 8 }}
        />
      </Modal>

      {/* Add Modal */}
      <Modal
        title="Add New User"
        visible={isAdding}
        onOk={addNewRecord}
        onCancel={() => setIsAdding(false)}
      >
        <Input
          placeholder="Username"
          value={newRecord.username}
          onChange={(e) => setNewRecord({ ...newRecord, username: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Email"
          value={newRecord.email}
          onChange={(e) => setNewRecord({ ...newRecord, email: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Role"
          value={newRecord.role}
          onChange={(e) => setNewRecord({ ...newRecord, role: e.target.value })}
          style={{ marginBottom: 8 }}
        />
        <Select
          placeholder="Select Project ID"
          value={newRecord.projectId || undefined}
          style={{ width: '100%', marginBottom: 8 }}
          onChange={(value) => {
            const selectedProj = projects.find((p) => p.project_id === value);
            setNewRecord({
              ...newRecord,
              projectId: selectedProj.project_id,
              projectName: selectedProj.project_name
            });
          }}
        >
          {projects.map((proj) => (
            <Option key={proj.project_id} value={proj.project_id}>
              {proj.project_id}
            </Option>
          ))}
        </Select>
        <Input
          placeholder="Project Name"
          value={newRecord.projectName}
          disabled
          style={{ marginBottom: 8 }}
        />
      </Modal>
    </div>
  );
};

export default UserManagement;